import React, { useContext, useRef } from "react";
import "@amzn/books-design-system-web/build/dist/src/components/BDS/Icon/Icon";
import styles from "./ShareButton.module.scss";
import DeviceContext from "src/contexts/DeviceContext";
import { getCurrentBaseUrl } from "src/utils/urlUtils";
import { getDetailsUrl } from "src/utils/asinUtils";
import debug from "src/utils/debugUtils";
import TranslationsContext from "src/contexts/TranslationsContext";
import { isSharingSupported, NativeAPI } from "src/utils/deviceUtils";
import { recordBehavioralMetric, recordOperationalMetric } from "src/utils/metricsUtils";
import focusHelper from "src/utils/focusHelper";

// Inline share SVGs so we can set colors using `currentColor` in `fill`/`stroke`
const ShareIconIOS = () => (
    <bds-icon iconName="share.svg" iconSize="30px"/>
);

type PropTypes = {
    item: QuickViewAsinMetadata;
};

const ShareButton: React.FC<PropTypes> = ({ item }: PropTypes) => {
    const context = useContext(DeviceContext);
    const translations = useContext(TranslationsContext);
    const self = useRef<HTMLButtonElement>(null);

    const sharingSupported = isSharingSupported();
    if (!sharingSupported && !debug.get("alwaysShowShare")) return null;

    return (
        <button
            type="button"
            className={`${styles[context.theme]} ${styles.shareButton}`}
            aria-label={translations.getText("share")}
            ref={self}
            onClick={() => {
                const shareTitle = translations.formatText("share-data-title", { title: item.title });
                const shareText = translations.formatText("share-data-text", { title: item.title, author: item.authors });

                const shareData: ShareData = {
                    title: shareTitle,
                    text: shareText,
                    // TODO add correct reftag
                    url: getDetailsUrl(getCurrentBaseUrl(), item.asin, "quick_view_ref_tag"),
                };

                debug.log(shareData);

                const metricsContext = { namespace: "Share", qv_asin: item.asin };
                recordBehavioralMetric(metricsContext, "Share.Click", 1);
                NativeAPI.share(shareData)
                    .then(() => {
                        recordBehavioralMetric(metricsContext, "Success", 1);
                        recordOperationalMetric(metricsContext, "shareButtonError", 0);
                    })
                    .catch(() => {
                        recordBehavioralMetric(metricsContext, "Success", 0);
                        recordOperationalMetric(metricsContext, "shareButtonError", 1.0);
                    })
                    .finally(() => focusHelper.requestFocus(self.current));
            }}
        >
            <ShareIconIOS/>
        </button>
    );
};

export default ShareButton;
